import * as React from "react";
import { ArrowLeft, MessageSquare, MessageSquareOff, Plus, Trash2 } from "lucide-react";
import { toast } from "react-toastify";
import { Loader } from "@/components/ui/loader";
import { Button } from "@/components/ui/button";
import { useDateFormatter } from "@/hooks/useDateFormatter";
import {
  useSnapshotConversationListQuery,
  useDeleteSnapshotConversationMutation,
} from "@/modules/snapshots/snapshotConversation.api";
import type { SnapshotConversation } from "./types";

interface Props {
  snapshotCollectionId: string;
  onSelectConversation: (conversation: SnapshotConversation) => void;
  onNewChat: () => void;
  onBack: () => void;
  contractFamilyProjection?: boolean;
}

export const SnapshotQaHistory: React.FC<Props> = ({
  snapshotCollectionId,
  onSelectConversation,
  onNewChat,
  onBack,
  contractFamilyProjection = false,
}) => {
  const { formatDateTime } = useDateFormatter();
  const { data, isLoading } = useSnapshotConversationListQuery({
    snapshot_collection_id: snapshotCollectionId,
    contract_family_projection: contractFamilyProjection,
  });
  const [deleteConversation] = useDeleteSnapshotConversationMutation();

  const handleDelete = async (e: React.MouseEvent, conversation: SnapshotConversation) => {
    e.stopPropagation();
    try {
      await deleteConversation({ conversation_id: conversation.id }).unwrap();
    } catch {
      toast.error("Failed to delete conversation");
    }
  };

  return (
    <div className="flex h-full flex-col">
      <div className="flex shrink-0 items-center justify-between border-b px-4 py-3">
        <button
          type="button"
          onClick={onBack}
          className="rounded p-1 text-gray-500 transition-colors hover:bg-gray-100 hover:text-gray-700"
        >
          <ArrowLeft className="h-4 w-4" />
        </button>
        <span className="text-sm font-semibold">History</span>
        <Button type="button" variant="ghost" size="xs" onClick={onNewChat} className="gap-1.5">
          <Plus className="h-3.5 w-3.5" />
          New Chat
        </Button>
      </div>

      <div className="min-h-0 flex-1 overflow-y-auto">
        {isLoading && (
          <div className="flex h-40 items-center justify-center">
            <Loader className="h-6 w-6 text-gray-400" />
          </div>
        )}

        {!isLoading && (!data || data.length === 0) && (
          <div className="flex h-40 flex-col items-center justify-center gap-2 px-4 text-muted-foreground">
            <MessageSquareOff className="h-8 w-8" />
            <p className="text-sm font-medium">No previous conversations</p>
          </div>
        )}

        {!isLoading && data && data.length > 0 && (
          <div className="flex flex-col">
            {data.map(conversation => {
              const displayTitle = conversation.title ?? "Untitled conversation";

              return (
                <div
                  key={conversation.id}
                  role="button"
                  tabIndex={0}
                  onClick={() => onSelectConversation(conversation)}
                  onKeyDown={e => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      onSelectConversation(conversation);
                    }
                  }}
                  className="group flex cursor-pointer items-start gap-3 border-b border-input px-4 py-3 text-left transition-colors hover:bg-ivory-mist-100"
                >
                  <MessageSquare className="mt-0.5 h-4 w-4 shrink-0 text-primary" />
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-sm">{displayTitle}</p>
                    <div className="mt-0.5 flex items-center gap-2">
                      <p className="text-xs text-muted-foreground">{formatDateTime(conversation.updated_at)}</p>
                      {conversation.message_count > 0 && (
                        <p className="text-xs text-muted-foreground">{conversation.message_count} messages</p>
                      )}
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={e => handleDelete(e, conversation)}
                    className="shrink-0 rounded p-1 text-gray-400 opacity-0 transition-opacity hover:text-destructive group-hover:opacity-100"
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </button>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};
